import React, { createContext, useState, useContext, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { apiFetch } from '../services/api';
import { Agency } from '../components/admin/AgencyManagementPage';
import { useAuth } from './AuthContext';

const isSiteAdmin = (): boolean => {
    const groupsStr = sessionStorage.getItem('groups');
    if (!groupsStr) return false;
    try {
        return JSON.parse(groupsStr).includes('site-admin');
    } catch {
        return false;
    }
};

const isOrgAdmin = (): boolean => {
    const groupsStr = sessionStorage.getItem('groups');
    if (!groupsStr) return false;
    try {
        return JSON.parse(groupsStr).includes('org-admin');
    } catch {
        return false;
    }
};

interface OriContextType {
    selectedOri: string | null;
    setSelectedOri: (ori: string) => void;
    availableOris: Agency[];
    isLoading: boolean;
    defaultOri: string | null;
    resetToDefaultOri: () => void;
    refreshAvailableOris: () => void;
    fetchAllAgencies: () => Promise<Agency[] | undefined>;
}

const OriContext = createContext<OriContextType | undefined>(undefined);

export const OriProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [selectedOri, setSelectedOriState] = useState<string | null>(null);
    const [availableOris, setAvailableOris] = useState<Agency[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [defaultOri, setDefaultOri] = useState<string | null>(null);
    const { isAuthenticated } = useAuth();

    const fetchAllAgencies = useCallback(async (): Promise<Agency[] | undefined> => {
        setIsLoading(true);
        try {
            const response = await apiFetch('/api/risc/agencies');
            if (!response.ok) throw new Error('Failed to fetch all agencies');
            const agencies: Agency[] = await response.json();
            // Defensive: drop malformed records (e.g. non-agency rows) so
            // downstream sorting/filtering on name/ori can't crash.
            return (agencies || []).filter(a => a && a.name && a.ori);
        } catch (error) {
            console.error("Failed to fetch all agencies", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchActiveAgencies = useCallback(async () => {
        if (!isAuthenticated) return;
        setIsLoading(true);
        try {
            const allAgencies = await fetchAllAgencies();
            if (!allAgencies) return;

            const currentUserOri = sessionStorage.getItem('agencyOri');
            setDefaultOri(currentUserOri);

            let userOris: Agency[];
            if (isSiteAdmin()) {
                userOris = allAgencies;
            } else if (isOrgAdmin()) {
                const userAgency = allAgencies.find(a => a.ori === currentUserOri);
                if (userAgency) {
                    const authorizedOris = new Set(userAgency.authorized_oris);
                    authorizedOris.add(currentUserOri!);
                    userOris = allAgencies.filter(a => authorizedOris.has(a.ori));
                } else {
                    userOris = [];
                }
            } else {
                const userAgency = allAgencies.find(a => a.ori === currentUserOri);
                userOris = userAgency ? [userAgency] : [];
            }

            const activeUserOris = userOris.filter(agency => agency.active);
            activeUserOris.sort((a, b) => {
                if (a.ori === currentUserOri) return -1;
                if (b.ori === currentUserOri) return 1;
                return a.name.localeCompare(b.name);
            });
            setAvailableOris(activeUserOris);

            // The selection is session-scoped and validated: a stale or
            // cross-session persisted ORI (e.g. a child agency picked in a
            // prior visit) would silently disable the merged authorized-agency
            // views, which only apply when viewing one's own agency.
            const storedOri = sessionStorage.getItem('selectedOri');
            const validStoredOri = storedOri && activeUserOris.some(o => o.ori === storedOri)
                ? storedOri
                : null;
            if (currentUserOri && activeUserOris.some(o => o.ori === currentUserOri)) {
                setSelectedOriState(validStoredOri || currentUserOri);
            } else if (activeUserOris.length > 0) {
                setSelectedOriState(validStoredOri || activeUserOris[0].ori);
            } else {
                setSelectedOriState(null);
            }
        } catch (error) {
            console.error("Failed to initialize ORI context", error);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, fetchAllAgencies]);

    useEffect(() => {
        fetchActiveAgencies();
    }, [fetchActiveAgencies]);

    const setSelectedOri = (ori: string) => {
        setSelectedOriState(ori);
        sessionStorage.setItem('selectedOri', ori);
    };

    const resetToDefaultOri = useCallback(() => {
        if (defaultOri) {
            setSelectedOri(defaultOri);
        }
    }, [defaultOri]);

    const value = useMemo(() => ({ selectedOri, setSelectedOri, availableOris, isLoading, defaultOri, resetToDefaultOri, fetchAllAgencies, refreshAvailableOris: fetchActiveAgencies }), [selectedOri, availableOris, isLoading, defaultOri, resetToDefaultOri, fetchAllAgencies, fetchActiveAgencies]);

    return <OriContext.Provider value={value}>{children}</OriContext.Provider>;
};

export const useOri = () => {
    const context = useContext(OriContext);
    if (context === undefined) {
        throw new Error('useOri must be used within an OriProvider');
    }
    return context;
};
